import { NsnCandidate } from '@/api/generatedApi';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

const HeadlineRow = styled(Box)({
  display: 'flex',
  alignItems: 'baseline',
  columnGap: '8px',
  flexWrap: 'wrap',
});

const MAX_DIFFERENCE_LENGTH = 60;

const truncate = (value: string): string =>
  value.length > MAX_DIFFERENCE_LENGTH ? `${value.slice(0, MAX_DIFFERENCE_LENGTH - 1)}…` : value;

const formatUnitPrice = ({ unitOfIssue, unitPrice }: NsnCandidate): string | undefined => {
  if (unitPrice === undefined) {
    return undefined;
  }
  const price = `$${unitPrice.toFixed(2)}`;
  return unitOfIssue ? `${unitOfIssue} ${price}` : price;
};

const describeSupplier = ({ companyName, cageCode }: NsnCandidate): string =>
  companyName ? `${companyName} (${cageCode})` : `CAGE ${cageCode}`;

const describeDifferences = (candidate: NsnCandidate): string[] => [
  ...candidate.distinguishingCharacteristics.map(characteristic => truncate(characteristic.value)),
  ...(candidate.endItemName ? [`End item: ${candidate.endItemName}`] : []),
];

export const nsnCandidateLabel = (candidate: NsnCandidate): string =>
  [candidate.nsn, ...describeDifferences(candidate).slice(0, 1)].join(' · ');

export const NsnCandidateSummary = ({ candidate }: { candidate: NsnCandidate }) => {
  const headlineDetails = [candidate.itemName, describeSupplier(candidate), formatUnitPrice(candidate)].filter(Boolean);
  const differences = describeDifferences(candidate);

  return (
    <Box>
      <HeadlineRow>
        <Typography variant='body2' fontWeight={600}>
          {candidate.nsn}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          {headlineDetails.join(' · ')}
        </Typography>
      </HeadlineRow>
      {differences.length > 0 && (
        <Typography variant='caption' color='text.secondary' data-testid='nsn-candidate-differences'>
          {differences.join(' · ')}
        </Typography>
      )}
    </Box>
  );
};
